import type { MessageThreadApi } from '@/modules/messaging/types';
import { CardEmptyState } from '@/modules/ui/components/card-empty-state/CardEmptyState';

import { ThreadListItem } from './ThreadListItem';

interface ThreadListProps {
  threads: MessageThreadApi[] | undefined;
  isLoading: boolean;
  selectedThreadId: string | null;
  onThreadSelect: (threadId: string) => void;
}

export const ThreadList = ({
  threads,
  isLoading,
  selectedThreadId,
  onThreadSelect,
}: ThreadListProps) => {
  const threadSummaries = (threads ?? []).map((thread) => {
    const unreadCount =
      thread.messages?.filter(
        (message) =>
          message.origin === 'Candidate' && message.status === 'Sent',
      ).length ?? 0;

    return {
      thread,
      unreadCount,
    };
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-5">
        <span className="text-muted-foreground">Loading conversations...</span>
      </div>
    );
  }

  if (threadSummaries.length === 0) {
    return <CardEmptyState title="No active conversations" />;
  }

  return (
    <div className="flex flex-col">
      {threadSummaries.map((data) => {
        const lastMessage = data.thread.messages
          ?.slice()
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          )
          .at(0);

        return (
          <ThreadListItem
            key={data.thread.id}
            message={{
              id: data.thread.id,
              candidate: data.thread.candidate,
              content: lastMessage?.content,
              createdAt: lastMessage?.createdAt,
              status: lastMessage?.status ?? 'Sent',
            }}
            unreadCount={data.unreadCount}
            isHighlighted={selectedThreadId === data.thread.id}
            status={data.thread.status}
            onClick={() => onThreadSelect(data.thread.id)}
          />
        );
      })}
    </div>
  );
};
